import type { WorkspaceIconName } from './icons';

export type SettingsGroup = 'personal' | 'company';
export type ViewGroup = 'workspace' | SettingsGroup | null;

/** The reading width a settings view needs: forms, tables or a dashboard with charts (DESIGN-SPEC §5.1). */
export type SettingsMeasure = 'form' | 'table' | 'wide';

export interface ViewDefinition {
  label: string;
  navigationLabel?: string;
  icon: WorkspaceIconName;
  group: ViewGroup;
  measure?: SettingsMeasure;
  /** A short word beside the sidebar label, e.g. "Early" for a section still in early access. */
  tag?: string;
  /** The server flag (AgentFeatures key) that must be on for the section to show. */
  feature?: 'storefront';
}

/** The one source of truth for workspace view keys and their shell metadata. */
export const viewDefinitions = {
  chat: { label: 'New chat', icon: 'chat', group: null },
  // Collaboration v1: the sidebar shows it with its badge (InboxNavItem).
  inbox: { label: 'Inbox', icon: 'inbox', group: null },
  // Projects v1: shared places for one piece of work. This is All projects; one project's home
  // is the chat view in it (#/workspace/chat?project=<id>).
  projects: { label: 'Projects', icon: 'project', group: 'workspace' },
  files: { label: 'Library', icon: 'files', group: null },
  results: { label: 'Library', icon: 'files', group: 'workspace' },
  // The operator's Apps place: the Library's authorized list filtered to Apps, drawn as cards.
  apps: { label: 'Apps', icon: 'app', group: 'workspace' },
  // Shopping assistant v1: shown only while the server's storefront flag is on.
  storefront: {
    label: 'Storefront',
    icon: 'store',
    group: 'workspace',
    tag: 'Early',
    feature: 'storefront',
  },
  meetings: { label: 'Meetings', icon: 'meetings', group: 'workspace' },
  skills: { label: 'Skills', icon: 'skills', group: 'workspace' },
  // Collaboration v1 (PR 4): saved jobs that run one published Skill.
  agents: { label: 'Agents', icon: 'agents', group: 'workspace' },
  knowledge: {
    label: 'Company knowledge',
    navigationLabel: 'Knowledge',
    icon: 'knowledge',
    group: 'workspace',
  },
  connections: { label: 'Connections', icon: 'connections', group: 'workspace' },
  account: { label: 'Account', icon: 'person', group: 'personal' },
  preferences: { label: 'Preferences', icon: 'settings', group: 'personal' },
  voice: { label: 'Voice', icon: 'meetings', group: 'personal' },
  'personal-memory': { label: 'Personal memory', icon: 'knowledge', group: 'personal' },
  desktop: { label: 'Downloads', icon: 'download', group: 'personal' },
  profile: { label: 'Company profile', icon: 'company', group: 'company' },
  processing: { label: 'AI processing', icon: 'settings', group: 'company' },
  models: { label: 'Models', icon: 'settings', group: 'company' },
  team: { label: 'Team', icon: 'person', group: 'company', measure: 'table' },
  billing: { label: 'Billing', icon: 'card', group: 'company' },
  usage: { label: 'Usage', icon: 'results', group: 'company', measure: 'wide' },
} as const satisfies Record<string, ViewDefinition>;

export type WorkspaceView = keyof typeof viewDefinitions;
export type WorkspacePageView = {
  [View in WorkspaceView]: (typeof viewDefinitions)[View]['group'] extends 'workspace'
    ? View
    : never;
}[WorkspaceView];

const viewKeys = Object.keys(viewDefinitions) as WorkspaceView[];

const viewsIn = (group: ViewGroup) =>
  viewKeys.filter((view) => viewDefinitions[view].group === group);

export const workspaceNavigation = viewsIn('workspace');

export const settingsNavigation = {
  personal: viewsIn('personal'),
  company: viewsIn('company'),
} as const;

export const settingsViews: WorkspaceView[] = [
  ...settingsNavigation.personal,
  ...settingsNavigation.company,
];

export const viewLabels = Object.fromEntries(
  viewKeys.map((view) => [view, viewDefinitions[view].label]),
) as Record<WorkspaceView, string>;

export const isSettingsView = (view: WorkspaceView) =>
  viewDefinitions[view].group === 'personal' || viewDefinitions[view].group === 'company';

export const isCompanySettingsView = (view: WorkspaceView) =>
  viewDefinitions[view].group === 'company';

export const settingsMeasure = (view: WorkspaceView): SettingsMeasure =>
  (viewDefinitions[view] as ViewDefinition).measure ?? 'form';

export function isWorkspacePageView(view: WorkspaceView): view is WorkspacePageView {
  return viewDefinitions[view].group === 'workspace';
}
